import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Plus, TrendingUp, TrendingDown, Sparkles, CheckCircle2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { CommandPalette } from "@/components/CommandPalette";
import { ProBanner } from "@/components/ProBanner";
import { UserGuide } from "@/components/UserGuide";
import { Footer } from "@/components/Footer";
import { api, formatAmount, CATEGORIES } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { formatDistanceToNowStrict } from "date-fns";

const CAT_COLORS = {
  food: "#00FF66",
  travel: "#00D4FF",
  rent: "#BF5AF2",
  entertainment: "#FF9500",
  shopping: "#FF3B30",
  utilities: "#FFD60A",
  general: "#8E8E93",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [insights, setInsights] = useState(null);
  const [friendsSummary, setFriendsSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("stb_guide_seen")) {
      // Auto-launch the guide once for new users
      const t = setTimeout(() => {
        setGuideOpen(true);
        localStorage.setItem("stb_guide_seen", "1");
      }, 600);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/summary");
        setSummary(data);
      } finally {
        setLoading(false);
      }
    })();
    api.get("/dashboard/insights").then(({ data }) => setInsights(data)).catch(() => {});
    api.get("/friends/summary").then(({ data }) => setFriendsSummary(data)).catch(() => {});
  }, []);

  const currency = summary?.currency || user?.currency || "USD";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar onOpenCmdK={() => setCmdkOpen(true)} />
      <CommandPalette open={cmdkOpen} setOpen={setCmdkOpen} />
      <ProBanner />

      <main className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/40 mb-2">/ Dashboard</div>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter" data-testid="dashboard-greeting">
              Hi, {user?.name?.split(" ")[0] || "friend"}.
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/groups">
              <Button variant="outline" className="border-border bg-foreground/[0.02] hover:bg-foreground/[0.05] h-10" data-testid="dashboard-new-group-btn">
                <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> New Group
              </Button>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={`skeleton-${i}`} className="h-40 bg-foreground/5" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-5">
            {/* Net Balance Card — hero widget */}
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="md:col-span-6 lg:col-span-7 rounded-2xl border border-border bg-card/40 p-7 relative overflow-hidden"
              data-testid="widget-net-balance"
            >
              <div className="absolute -top-16 -right-16 h-52 w-52 rounded-full blur-3xl opacity-40" style={{ background: summary.net_balance >= 0 ? "var(--brand)" : "#FF3B30" }} />
              <div className="relative">
                <div className="text-xs font-mono uppercase tracking-[0.25em] text-foreground/40 mb-3">Net Balance</div>
                <div className="flex items-end gap-3 mb-6">
                  <div className={`font-mono text-6xl sm:text-7xl font-semibold tracking-tighter ${summary.net_balance >= 0 ? "brand-text" : "text-red-400"}`} data-testid="net-balance-amount">
                    {summary.net_balance >= 0 ? "+" : "-"}{formatAmount(summary.net_balance, currency)}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6 pt-6 border-t border-border">
                  <div>
                    <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] text-foreground/40 mb-1.5">
                      <TrendingUp className="h-3 w-3" strokeWidth={1.5} />
                      Owed to you
                    </div>
                    <div className="font-mono text-2xl font-medium" data-testid="owed-to-me">{formatAmount(summary.total_owed_to_me, currency)}</div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] text-foreground/40 mb-1.5">
                      <TrendingDown className="h-3 w-3" strokeWidth={1.5} />
                      You owe
                    </div>
                    <div className="font-mono text-2xl font-medium text-foreground/80" data-testid="i-owe">{formatAmount(summary.total_i_owe, currency)}</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Friends Overview — full width row */}
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.08 }}
              className="md:col-span-12 lg:col-span-12 rounded-2xl border border-border bg-card/40 p-6"
              data-testid="widget-friends-overview"
            >
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/40 mb-1">Friends Overview</div>
                  <div className="text-sm text-foreground/60">Who owes whom, right now</div>
                </div>
                {friendsSummary && (friendsSummary.incoming > 0 || friendsSummary.outgoing > 0) && (
                  <div className="flex items-center gap-4 text-xs font-mono">
                    <div>
                      <span className="uppercase tracking-wider text-foreground/40">In</span>{" "}
                      <span className="brand-text tabular-nums" data-testid="friends-total-incoming">{formatAmount(friendsSummary.incoming, currency)}</span>
                    </div>
                    <div>
                      <span className="uppercase tracking-wider text-foreground/40">Out</span>{" "}
                      <span className="text-red-400 tabular-nums" data-testid="friends-total-outgoing">{formatAmount(friendsSummary.outgoing, currency)}</span>
                    </div>
                  </div>
                )}
              </div>
              {(() => {
                if (!friendsSummary) return <div className="text-sm text-foreground/40 py-6 text-center">Loading…</div>;
                const outstanding = (friendsSummary.friends || []).filter((f) => Math.abs(f.net) > 0.01);
                if (outstanding.length === 0) {
                  return (
                    <div className="flex flex-col items-center gap-3 py-8" data-testid="friends-overview-empty">
                      <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center">
                        <CheckCircle2 className="h-6 w-6 text-green-400" strokeWidth={1.75} />
                      </div>
                      <div className="text-sm brand-text font-mono uppercase tracking-wider">All settled up with everyone</div>
                    </div>
                  );
                }
                return (
                  <div className="divide-y divide-border">
                    {outstanding.map((f) => (
                      <Link
                        key={f.id}
                        to={`/friends/${f.id}?tab=overview`}
                        className="flex items-center justify-between py-3 gap-3 hover:bg-foreground/[0.02] rounded-lg px-2 -mx-2 transition"
                        data-testid={`friend-row-${f.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-10 w-10 rounded-xl bg-foreground/5 flex items-center justify-center text-lg shrink-0">{f.avatar_emoji || "🙂"}</div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{f.name}</div>
                            <div className="text-[11px] font-mono text-foreground/50 truncate">{f.breakdown}</div>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className={`font-mono text-base font-semibold tabular-nums ${f.net > 0 ? "brand-text" : "text-red-400"}`}>
                            {f.net > 0 ? "+" : "-"}{formatAmount(Math.abs(f.net), currency)}
                          </div>
                          <div className="text-[10px] font-mono uppercase tracking-wider text-foreground/40">
                            {f.net > 0 ? "owes you" : "you owe"}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                );
              })()}
            </motion.div>

            {/* AI Insights */}
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
              className="md:col-span-6 lg:col-span-5 rounded-2xl border border-border bg-card/40 p-6 relative overflow-hidden tracing-beam"
              data-testid="widget-ai-insights"
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="h-7 w-7 rounded-md brand-bg text-black flex items-center justify-center">
                  <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
                </div>
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/60">AI Insights</div>
              </div>
              <p className="text-lg leading-snug mb-4" data-testid="ai-insight-text">
                {insights?.insight || "Add a few bills to unlock personalized insights."}
              </p>
              {insights?.tips?.length > 0 && (
                <ul className="space-y-2">
                  {insights.tips.slice(0, 3).map((tip, idx) => (
                    <li key={`tip-${idx}-${String(tip).slice(0, 20)}`} className="text-sm text-foreground/60 flex items-start gap-2">
                      <span className="h-1 w-1 rounded-full brand-bg mt-2 shrink-0" />
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>

            {/* Spending Overview */}
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
              className="md:col-span-6 lg:col-span-7 rounded-2xl border border-border bg-card/40 p-6"
              data-testid="widget-spending"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/40">Monthly Spending</div>
                <div className="text-xs font-mono text-foreground/40">{currency}</div>
              </div>
              {Object.keys(summary.spending_by_month).length === 0 ? (
                <div className="h-48 flex items-center justify-center text-sm text-foreground/40">No data yet — add a bill</div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={Object.entries(summary.spending_by_month).map(([month, value]) => ({ month, value }))}>
                    <XAxis dataKey="month" stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                    <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={40} />
                    <Tooltip cursor={{ fill: "rgba(255,255,255,0.03)" }} contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontFamily: "JetBrains Mono" }} />
                    <Bar dataKey="value" fill="var(--brand)" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </motion.div>

            {/* Category Donut */}
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
              className="md:col-span-6 lg:col-span-5 rounded-2xl border border-border bg-card/40 p-6"
              data-testid="widget-category"
            >
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/40 mb-5">By Category</div>
              {Object.keys(summary.spending_by_category).length === 0 ? (
                <div className="h-48 flex items-center justify-center text-sm text-foreground/40">No spending categorized yet</div>
              ) : (
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width={160} height={160}>
                    <PieChart>
                      <Pie
                        data={Object.entries(summary.spending_by_category).map(([name, value]) => ({ name, value }))}
                        dataKey="value" innerRadius={44} outerRadius={70} paddingAngle={2} strokeWidth={0}
                      >
                        {Object.keys(summary.spending_by_category).map((c) => (
                          <Cell key={c} fill={CAT_COLORS[c] || "#8E8E93"} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-1.5">
                    {Object.entries(summary.spending_by_category).map(([cat, val]) => {
                      const info = CATEGORIES.find((c) => c.value === cat);
                      return (
                        <div key={cat} className="flex items-center justify-between text-xs" data-testid={`cat-row-${cat}`}>
                          <span className="flex items-center gap-1.5">
                            <span className="h-2 w-2 rounded-sm" style={{ background: CAT_COLORS[cat] || "#8E8E93" }} />
                            <span className="capitalize text-foreground/70">{info?.label || cat}</span>
                          </span>
                          <span className="font-mono text-foreground/60">{formatAmount(val, currency)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>

            {/* Who Owes You Most */}
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
              className="md:col-span-6 lg:col-span-5 rounded-2xl border border-border bg-card/40 p-6"
              data-testid="widget-top-creditors"
            >
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/40 mb-5">Who Owes You Most</div>
              {summary.top_creditors.length === 0 ? (
                <div className="text-sm text-foreground/40 py-8 text-center">Nobody owes you right now 🎉</div>
              ) : (
                <div className="space-y-2">
                  {summary.top_creditors.map((t, idx) => (
                    <div key={t.user.id} className="flex items-center justify-between rounded-lg px-3 py-2.5 bg-foreground/[0.02] border border-border" data-testid={`creditor-row-${idx}`}>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-[10px] text-foreground/30 w-5">0{idx + 1}</span>
                        <span className="h-8 w-8 rounded-full bg-foreground/10 flex items-center justify-center text-sm">{t.user.avatar_emoji || "🙂"}</span>
                        <span className="text-sm">{t.user.name}</span>
                      </div>
                      <span className="font-mono brand-text">+{formatAmount(t.amount, currency)}</span>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Active Groups */}
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.25 }}
              className="md:col-span-6 lg:col-span-7 rounded-2xl border border-border bg-card/40 p-6"
              data-testid="widget-active-groups"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/40">Active Groups</div>
                <Link to="/groups" className="text-xs text-foreground/40 hover:text-white flex items-center gap-1" data-testid="dashboard-view-all-groups">
                  View all <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} />
                </Link>
              </div>
              {summary.active_groups.length === 0 ? (
                <div className="text-sm text-foreground/40 py-12 text-center">
                  <Link to="/groups" className="brand-text hover:underline" data-testid="empty-create-group-link">
                    Create your first group →
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {summary.active_groups.slice(0, 4).map((g) => (
                    <Link
                      key={g.id} to={`/groups/${g.id}`}
                      className="group rounded-lg border border-border bg-foreground/[0.02] p-4 hover:border-border transition"
                      data-testid={`group-card-${g.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="h-10 w-10 rounded-lg bg-foreground/5 flex items-center justify-center text-xl">{g.emoji}</div>
                        <ArrowUpRight className="h-4 w-4 text-foreground/30 group-hover:text-white transition" strokeWidth={1.5} />
                      </div>
                      <div className="mt-3 font-medium truncate">{g.name}</div>
                      <div className="mt-1 text-xs text-foreground/40 font-mono uppercase tracking-wider">
                        {g.bill_count} bills · {g.member_count} people
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Recent Bills */}
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
              className="md:col-span-6 lg:col-span-12 rounded-2xl border border-border bg-card/40 p-6"
              data-testid="widget-recent-bills"
            >
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/40 mb-5">Recent Bills</div>
              {summary.recent_bills.length === 0 ? (
                <div className="text-sm text-foreground/40 py-12 text-center">No bills yet. Add one inside a group.</div>
              ) : (
                <div className="divide-y divide-border">
                  {summary.recent_bills.map((b) => (
                    <Link
                      key={b.id} to={`/groups/${b.group_id}`}
                      className="flex items-center justify-between py-3 hover:bg-foreground/[0.02] px-2 -mx-2 rounded-md transition"
                      data-testid={`recent-bill-${b.id}`}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <span className="h-10 w-10 rounded-lg bg-foreground/5 flex items-center justify-center text-lg shrink-0">
                          {CATEGORIES.find((c) => c.value === b.category)?.emoji || "💸"}
                        </span>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{b.title}</div>
                          <div className="text-xs text-foreground/40 font-mono uppercase tracking-wider truncate">
                            {b.group_emoji} {b.group_name} · paid by {b.paid_by_name}
                          </div>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-mono">{formatAmount(b.amount, b.currency)}</div>
                        <div className="text-[10px] font-mono uppercase text-foreground/30 tracking-wider">
                          {formatDistanceToNowStrict(new Date(b.created_at), { addSuffix: true })}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        )}
      </main>
      <Footer />
      <UserGuide open={guideOpen} onOpenChange={setGuideOpen} />
    </div>
  );
}
